import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Spinner } from "components/DesignSystem/Loader/loader";
import Async from "components/DesignSystem/AsyncComponents/Async";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "components/DesignSystem/ToggleGroup/ToggleGroup";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  AnalysisType,
  ReportType,
  useGetAnalysisQuery,
} from "store/apis/financialMetrics";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { setLedgerFilter } from "store/slices/generalLedger";
import { TrendSubtitle } from "./FinancialSummary";
import { TopCategoriesChart } from "../HorizontalBarChart/TopCategoriesChart";
import { CategoriesOverTimeChart } from "../HorizontalBarChart/CategoriesOverTimeChart";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";

type RevenueAnalysisProps = {
  startDate: string;
  endDate: string;
  periodText: string;
  reportType: ReportType;
  forcedAnalysisType?: AnalysisType;
  isCustomDateRange?: boolean;
};

const REPORT_CONFIG: Record<
  ReportType,
  { title: string; totalLabel: string; loadingText: string }
> = {
  REVENUE: {
    title: "Revenue Analysis",
    totalLabel: "Total Revenue",
    loadingText: "Loading Revenue Analysis...",
  },
  SPEND: {
    title: "Spend Analysis",
    totalLabel: "Total Spend",
    loadingText: "Loading Spend Analysis...",
  },
};

export const RevenueAnalysis = ({
  startDate,
  endDate,
  periodText,
  reportType,
  forcedAnalysisType,
  isCustomDateRange,
}: RevenueAnalysisProps) => {
  const entityId = useCurrentEntityId();
  const dispatch = useDispatch();
  const history = useHistory();
  const storageKey = `financial_analysis_type_${reportType}`;
  const { link } = useConstructInternalLink();
  const [analysisType, setAnalysisType] = useState<AnalysisType>(() => {
    if (forcedAnalysisType) return forcedAnalysisType;
    const stored = localStorage.getItem(storageKey);
    if (stored === "CATEGORY" || stored === "CLASSIFICATION") return stored;
    return "CATEGORY";
  });
  const config = REPORT_CONFIG[reportType];

  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );

  const handleAnalysisTypeChange = (value: string) => {
    if (!value || forcedAnalysisType) return;
    setAnalysisType(value as AnalysisType);
    localStorage.setItem(storageKey, value);
  };

  const handleCategoryClick = useCallback(
    (categoryId: string) => {
      if (!categoryId) return;

      dispatch(
        setLedgerFilter({
          categoryIds: [categoryId],
          ...(bookkeepingSettings?.accounting_method && {
            accountingMethod: bookkeepingSettings.accounting_method,
          }),
        })
      );
      history.push(
        link(`/books/categories`, { moreQuery: { startDate, endDate } })
      );
    },
    [entityId, startDate, endDate, dispatch, history, bookkeepingSettings]
  );

  const { data, isLoading, isSuccess, isError } = useGetAnalysisQuery(
    {
      entityId,
      start_date: startDate,
      end_date: endDate,
      report_type: reportType,
      analysis_type: analysisType,
      is_custom_date_range: isCustomDateRange || undefined,
    },
    { skip: !entityId }
  );

  const changePercentage = data?.change_percentage ?? null;
  const direction = (changePercentage ?? 0) >= 0 ? "up" : "down";

  return (
    <div className="t-flex t-flex-col t-gap-4 t-bg-surface t-border t-border-solid t-border-neutral-10 t-rounded-lg t-p-5 sm:t-p-4 sm:t-px-3">
      <div className="t-pb-4 t-border-b t-border-solid t-border-neutral-10 t-border-x-0 t-border-t-0">
        <span className="t-text-subtitle-lg t-text-text-black">
          {config.title}
        </span>
      </div>
      <Async.Root
        isLoading={isLoading}
        isSuccess={isSuccess}
        isEmpty={false}
        isError={isError}
        customLoader={
          <div className="t-flex t-flex-col t-justify-center t-items-center t-h-96 t-gap-3">
            <Spinner />
            <span className="t-text-body-sm t-text-text-30">
              {config.loadingText}
            </span>
          </div>
        }
      >
        <Async.ErrorHandler>
          <div className="t-flex t-justify-center t-items-center t-h-96 t-text-red-50">
            Something went wrong
          </div>
        </Async.ErrorHandler>
        <Async.Success>
          {data && (
            <div className="t-flex t-flex-col t-gap-6">
              <div className="t-flex t-justify-between t-items-start sm:t-flex-col sm:t-gap-3">
                <div className="t-flex t-flex-col t-gap-1">
                  <span className="t-text-body-sm t-text-text-30">
                    {config.totalLabel}
                  </span>
                  <span className="t-text-body-sm t-font-medium t-text-text-60 sm:t-text-subtitle sm:t-text-text-100">
                    <AmountSuperScript amount={data.total_amount} />
                  </span>
                  <span className="t-text-body-sm">
                    <TrendSubtitle
                      percentage={
                        changePercentage !== null
                          ? Math.abs(changePercentage)
                          : null
                      }
                      period={periodText}
                      direction={direction}
                      invertColors={reportType === "SPEND"}
                    />
                  </span>
                </div>
                {forcedAnalysisType ? (
                  <span className="t-text-body-sm t-text-text-60 t-font-medium t-capitalize">
                    View by:{" "}
                    {forcedAnalysisType === "CATEGORY"
                      ? "Category"
                      : "Classification"}
                  </span>
                ) : (
                  <ToggleGroup
                    value={analysisType}
                    onValueChange={handleAnalysisTypeChange}
                  >
                    <ToggleGroupItem value="CATEGORY">Category</ToggleGroupItem>
                    <ToggleGroupItem value="CLASSIFICATION">
                      Classification
                    </ToggleGroupItem>
                  </ToggleGroup>
                )}
              </div>

              <div className="t-flex t-gap-8 sm:t-flex-col sm:t-gap-6">
                <div className="t-flex t-flex-col t-gap-4 t-w-1/2 sm:t-w-full">
                  <span className="t-text-body-sm t-text-text-60 t-font-medium">
                    {analysisType === "CATEGORY"
                      ? "Top categories"
                      : "Top classifications"}
                  </span>
                  <TopCategoriesChart
                    categories={data.top_items}
                    analysisType={analysisType}
                    chartId={reportType}
                    onCategoryClick={handleCategoryClick}
                  />
                </div>

                <div className="t-flex t-flex-col t-gap-4 t-w-1/2 sm:t-w-full">
                  <span className="t-text-body-sm t-text-text-60 t-font-medium">
                    {analysisType === "CATEGORY"
                      ? "Categories over time"
                      : "Classifications over time"}
                  </span>
                  <CategoriesOverTimeChart
                    categoriesOverTime={data.items_over_time}
                    startDate={startDate}
                    endDate={endDate}
                    emptyLabelType={
                      analysisType === "CLASSIFICATION"
                        ? "classification"
                        : "category"
                    }
                  />
                </div>
              </div>
            </div>
          )}
        </Async.Success>
      </Async.Root>
    </div>
  );
};
